import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, renderList as _renderList, Fragment as _Fragment, createVNode as _createVNode, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "body-area" }
const _hoisted_2 = {
  key: 0,
  class: "spinner-layer"
}
const _hoisted_3 = { class: "timeline" }
const _hoisted_4 = {
  key: 0,
  class: "component-box no-parent-placeholder mr8 mb8 pd6"
}
const _hoisted_5 = {
  key: 0,
  class: "error"
}
const _hoisted_6 = { key: 0 }
const _hoisted_7 = {
  key: 1,
  class: "mr8 mb6"
}
const _hoisted_8 = { key: 2 }

import {
  reactive,
  computed,
  watch,
  nextTick,
  useTemplateRef,
  onMounted,
} from 'vue';
import type { MapElemInfo, FetchGeoItemsParams, QueryArea } from 'src/models';
import type { GIComment } from 'src/models/geoItem';
import MapElemInfoComment from './MapElemInfoComment.vue';
import type { Comment } from 'src/models/comment';

interface ContainerElement extends MapElemInfo<GIComment> {
  relativeComments: GIComment[];
}

interface MapElemInfoCommentContainerState {
  isStartReply: boolean;
  collapseNumber: number;
  isFetchingData: boolean;
  showFetchDataError: boolean;
}

// 子コンポーネント(MapElemInfoComment)から受け取り、親へそのまま中継するペイロード型
type ShowImageParams = {
  modalTitle: string;
  imageUrls: string[];
  downloadFilenameTemplate: string;
  initialIndex: number;
};
type CommentMutationPayload = { dataName: string; data: Comment };
type ConfirmDeletePayload = { dataName: string; data: Comment; confirmMessage: string };

interface Props {
  elem: ContainerElement;
  createFailed: number;
  updateFailed: number;
  fetchGeoItemFailed: number;
}

const fetchDataErrorMessage = '付箋の閲覧権限がありません。';


export default /*@__PURE__*/_defineComponent({
  __name: 'MapElemInfoCommentContainer',
  props: {
    elem: {},
    createFailed: {},
    updateFailed: {},
    fetchGeoItemFailed: {}
  },
  emits: ["update", "create", "confirm-delete", "cancel-edit", "show-image", "show-previous-geo-item", "show-next-geo-item", "area-movie-search", "fetch-geo-item"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const state = reactive<MapElemInfoCommentContainerState>({
  isStartReply: false,
  collapseNumber: 5,
  isFetchingData: false,
  showFetchDataError: false,
});
const refContainer = useTemplateRef<HTMLElement>('refContainer');

const scrollToTargetComment = (target: HTMLElement) => {
  if (!refContainer.value) return;

  const containerRect = refContainer.value.getBoundingClientRect();
  const targetRect = target.getBoundingClientRect();
  const isViewable = targetRect.top >= containerRect.top &&
    targetRect.bottom <= containerRect.top + refContainer.value.clientHeight;
  if (!isViewable) {
    const scrollTop = targetRect.top - containerRect.top;
    const scrollBot = targetRect.bottom - containerRect.bottom;
    if (Math.abs(scrollTop) < Math.abs(scrollBot)) {
      refContainer.value.scrollTop += scrollTop;
    } else {
      refContainer.value.scrollTop += scrollBot;
    }
  }
};
const refCommentElems = useTemplateRef<HTMLElement[]>('refCommentElems');
const displayTargetComment = () => {
  nextTick(() => {
    const targetIdx = displayedComments.value.findIndex(comment => comment.data.id === props.elem.data.id);
    if (targetIdx >= 0 && refCommentElems.value) {
      const targetElem = refCommentElems.value[targetIdx];
      scrollToTargetComment(targetElem);
    }
  });
};
const initDisplay = () => {
  state.isStartReply = false;
  state.collapseNumber = 5;
  state.isFetchingData = false;
  state.showFetchDataError = false;
  displayTargetComment();
};
onMounted(initDisplay);
watch(() => props.elem, initDisplay);
watch(() => props.fetchGeoItemFailed, () => {
  if (props.fetchGeoItemFailed > 0) {
    state.showFetchDataError = true;
  }
});
const relativeComments = computed(() => {
  return props.elem.relativeComments || [];
});
const displayedComments = computed(() => {
  const allComments = [
    props.elem,
    ...relativeComments.value.map(comment => ({
      dataName: props.elem.dataName,
      data: comment,
    })),
  ].sort((a, b) => {
    if (a.data.isParentComment) return -1;
    if (b.data.isParentComment) return 1;

    return a.data.ts && b.data.ts && a.data.ts < b.data.ts ? -1
      : a.data.ts && b.data.ts && a.data.ts > b.data.ts ? 1 : 0;
  });
  if (state.collapseNumber && allComments.length >= state.collapseNumber) {
    return [allComments[0], allComments[allComments.length - 1]];
  }
  return allComments;
});
const parentComment = computed(() => {
  return relativeComments.value.find(e => e.id === props.elem.data.parentId);
});
const initialCommentForReply = computed(() => {
  return {
    dataName: props.elem.dataName,
    data: {
      lat: props.elem.data.location.lat,
      lon: props.elem.data.location.lon,
      parentId: props.elem.data.parentId || props.elem.data.id,
      kpUid: parentComment.value?.kpUid || props.elem.data.kpUid,
    },
  };
});
// methods
const refNewCommentElem = useTemplateRef<HTMLElement>('refNewCommentElem');
const startReply = () => {
  state.isStartReply = true;
  nextTick(() => {
    if (!refNewCommentElem.value) return;

    scrollToTargetComment(refNewCommentElem.value);
  });
};
const onChildComponentUpdate = (obj: CommentMutationPayload) => {
  if (obj.data.id) {
    emit('update', obj);
    return;
  }
  emit('create', obj);
};
const confirmDelete = (obj: ConfirmDeletePayload) => {
  emit('confirm-delete', obj);
};
const cancelReply = () => {
  state.isStartReply = false;
  emit('cancel-edit');
};
const disableCollapse = () => {
  state.collapseNumber = 0;
};
const showImage = (obj: ShowImageParams) => {
  emit('show-image', obj);
};
const showPrevious = () => {
  emit('show-previous-geo-item', props.elem);
};
const showNext = () => {
  emit('show-next-geo-item', props.elem);
};
const doAreaMovieSearch = (queryArea: QueryArea) => {
  emit('area-movie-search', queryArea);
};
const searchParent = () => {
  // 連打防止
  if (state.isFetchingData) return;

  // 親側で再表示する際に、isFetchingDataがfalseに戻る
  state.isFetchingData = true;
  const evtObj: FetchGeoItemsParams = {
    dataName: props.elem.dataName,
    id: props.elem.data.parentId,
  };
  emit('fetch-geo-item', evtObj);
};


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: "comment-refContainer",
    ref_key: "refContainer",
    ref: refContainer
  }, [
    _createElementVNode("div", _hoisted_1, [
      (state.isFetchingData)
        ? (_openBlock(), _createElementBlock("div", _hoisted_2, [...(_cache[1] || (_cache[1] = [
            _createElementVNode("i", { class: "mr8 fa-3x fas fa-spinner fa-spin" }, null, -1)
          ]))]))
        : _createCommentVNode("", true),
      _createElementVNode("ul", _hoisted_3, [
        (__props.elem.data.isChildComment && !parentComment.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
              _cache[4] || (_cache[4] = _createElementVNode("h5", { class: "mb0 mr12" }, "返信元付箋", -1)),
              _createElementVNode("button", {
                class: "btn btn-sm btn-secondary",
                onClick: searchParent
              }, " 付箋を取得 "),
              (state.showFetchDataError)
                ? (_openBlock(), _createElementBlock("div", _hoisted_5, _toDisplayString(fetchDataErrorMessage)))
                : _createCommentVNode("", true),
              _cache[5] || (_cache[5] = _createElementVNode("div", { class: "spacer" }, null, -1)),
              _createElementVNode("div", { class: "move-buttons mt2" }, [
                _createElementVNode("button", {
                  class: "btn btn-primary btn-xs mr4",
                  onClick: showPrevious
                }, [...(_cache[2] || (_cache[2] = [
                  _createElementVNode("i", { class: "fas fa-arrow-left" }, null, -1),
                  _createTextVNode(" 前へ ", -1)
                ]))]),
                _createElementVNode("button", {
                  class: "btn btn-primary btn-xs",
                  onClick: showNext
                }, [...(_cache[3] || (_cache[3] = [
                  _createTextVNode(" 次へ ", -1),
                  _createElementVNode("i", { class: "fas fa-arrow-right" }, null, -1)
                ]))])
              ])
            ]))
          : _createCommentVNode("", true),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(displayedComments.value, (comment, index) => {
          return (_openBlock(), _createElementBlock("div", {
            key: comment.data.id,
            ref_for: true,
            ref_key: "refCommentElems",
            ref: refCommentElems
          }, [
            _createElementVNode("div", {
              class: _normalizeClass(["component-box mr8 mb8", { ml18: comment.data.isChildComment }])
            }, [
              _createVNode(MapElemInfoComment, {
                elem: comment,
                "update-failed": __props.updateFailed,
                onShowImage: showImage,
                "show-move-buttons": comment.data.isParentComment,
                onShowPrevious: showPrevious,
                onShowNext: showNext,
                onAreaMovieSearch: doAreaMovieSearch,
                onUpdate: onChildComponentUpdate,
                onConfirmDelete: confirmDelete,
                onCancelEdit: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('cancel-edit')))
              }, null, 8, ["elem", "update-failed", "show-move-buttons"])
            ], 2),
            (state.collapseNumber && index === 0 && displayedComments.value.length !== relativeComments.value.length + 1)
              ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                  _createElementVNode("button", {
                    type: "button",
                    class: "btn btn-lg btn-block btn-secondary mb6",
                    onClick: disableCollapse
                  }, [
                    _createTextVNode(" もっと見る（" + _toDisplayString(relativeComments.value.length + 1 - displayedComments.value.length) + "件） ", 1),
                    _cache[6] || (_cache[6] = _createElementVNode("i", { class: "fas fa-chevron-down" }, null, -1))
                  ])
                ]))
              : _createCommentVNode("", true)
          ]))
        }), 128))
      ]),
      (!state.isStartReply)
        ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
            _createElementVNode("button", {
              type: "button",
              key: "reply",
              class: "btn btn-sm btn-primary",
              onClick: startReply
            }, [...(_cache[7] || (_cache[7] = [
              _createElementVNode("i", { class: "fas fa-reply mr2" }, null, -1),
              _createTextVNode("返信 ", -1)
            ]))])
          ]))
        : (_openBlock(), _createElementBlock("div", _hoisted_8, [
            _createElementVNode("div", {
              class: "component-box mr8 mb8",
              ref_key: "refNewCommentElem",
              ref: refNewCommentElem
            }, [
              _createVNode(MapElemInfoComment, {
                elem: initialCommentForReply.value,
                "create-failed": __props.createFailed,
                "is-create-single-new-element": true,
                "force-show-cancel-button": true,
                onUpdate: onChildComponentUpdate,
                onCancelEdit: cancelReply
              }, null, 8, ["elem", "create-failed"])
            ], 512)
          ]))
    ])
  ], 512))
}
}

})